import {Message} from 'primeng/message';
import {Component, inject, OnInit, viewChild} from '@angular/core';
import {Button} from 'primeng/button';
import {translate, TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {ChangePasswordDialogComponent} from '../../../shared/components/dialog/change-password-dialog/change-password-dialog.component';
import {Fluid} from 'primeng/fluid';
import {FormFieldComponent} from '../../../shared/components/inputs/form-field/form-field.component';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormValidationService} from '../../../shared/services/form-validation.service';
import {ToastService} from '@frontend/toast';
import {DialogService, DynamicDialogModule} from 'primeng/dynamicdialog';
import {AuthService} from '../../../shared/services/auth.service';
import {finalize, Observable} from 'rxjs';
import {RoleCheckService} from '../../../shared/services/role-check.service';
import {Select} from 'primeng/select';
import {DirectusLoader, Language} from '@frontend/directus';
import {UserProfileService} from '../../../shared/services/user-profile.service';
import {UserlaneService} from '../../../shared/services/userlane.service';
import {
  CustomerUserControllerService,
  CustomerUserResponse,
  ProviderUserControllerService,
  ProviderUserResponse,
  UpdateCurrentCustomerUserInvoiceDataRequestParams,
  UpdateCurrentCustomerUserRequestParams,
  UpdateCurrentProviderUserRequestParams,
} from '@frontend/shared-api';
import {BillingFormComponent} from '../../../shared/components/billing/billing-form/billing-form.component';
import {NewsletterSubscriptionComponent} from './newsletter-subscription/newsletter-subscription.component';
import {environment} from '../../../../environments/environment';

@Component({
  selector: 'app-user-profile',
  imports: [
    Button,
    TranslocoDirective,
    Fluid,
    FormFieldComponent,
    ReactiveFormsModule,
    TranslocoPipe,
    DynamicDialogModule,
    Select,
    BillingFormComponent,
    Message,
    NewsletterSubscriptionComponent,
  ],
  templateUrl: './user-profile.component.html',
})
export class UserProfileComponent implements OnInit {
  private static readonly IDENTITY_PROVIDER_FIELDS = ['firstName', 'lastName', 'phoneNumber'];

  readonly billingForm = viewChild(BillingFormComponent);

  private readonly fb = inject(FormBuilder);
  private readonly customerUserService = inject(CustomerUserControllerService);
  private readonly providerUserService = inject(ProviderUserControllerService);
  private readonly toast = inject(ToastService);
  private readonly dialogService = inject(DialogService);
  private readonly authService = inject(AuthService);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly translocoService = inject(TranslocoService);
  private readonly directusLoader = inject(DirectusLoader);
  private readonly userProfileService = inject(UserProfileService);
  private readonly userlaneService = inject(UserlaneService);

  submitting = false;
  editing = false;
  submittingBilling = false;
  editingBilling = false;
  scimManaged = false;
  languageOptions: {label: string; value: string}[] = [];
  form!: FormGroup;

  ngOnInit(): void {
    this.createForm();
    this.updateLanguageOptions();

    if (this.roleCheckService.hasSelecthorAdminRole()) {
      return;
    }

    this.getCurrentUserDataFromAPI().subscribe((userData) => {
      this.populateFormWithUserData(userData);
    });
  }

  private createForm(): void {
    const isCustomerAdmin = this.roleCheckService.hasCustomerAdminRole();
    const commonControls = {
      firstName: [{value: '', disabled: !this.editing}, Validators.required],
      lastName: [{value: '', disabled: !this.editing}, Validators.required],
      languageCode: [{value: '', disabled: !this.editing}, Validators.required],
      phoneNumber: [
        {value: '', disabled: !this.editing},
        [Validators.required, FormValidationService.phoneValidator],
      ],
    };

    if (this.isProviderUser()) {
      this.form = this.fb.group(commonControls);
    } else {
      const customerControls = {
        ...commonControls,
        companyName: [
          {value: '', disabled: !this.editing},
          isCustomerAdmin ? [Validators.required] : [],
        ],
        email: [
          {value: '', disabled: !this.editing},
          isCustomerAdmin ? [Validators.required, Validators.email] : [Validators.email],
        ],
      };

      this.form = this.fb.group(customerControls);
    }
  }

  onEdit() {
    this.editing = true;
    this.toggleFormControlsState();
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;
    const updateRequest$ = this.getUpdateRequest();

    updateRequest$
      .pipe(
        finalize(() => {
          this.submitting = false;
        }),
      )
      .subscribe({
        next: () => {
          this.handleUpdateSuccess();
        },
      });
  }

  private getUpdateRequest(): Observable<CustomerUserResponse | ProviderUserResponse> {
    if (this.isProviderUser()) {
      const request = this.buildProviderUpdateRequest();
      return this.providerUserService.updateCurrentProviderUser(request);
    } else {
      const request = this.buildCustomerUpdateRequest();
      return this.customerUserService.updateCurrentCustomerUser(request);
    }
  }

  private async handleUpdateSuccess(): Promise<void> {
    const newLang = this.form.get('languageCode')?.value;

    if (newLang && newLang !== this.translocoService.getActiveLang()) {
      this.translocoService.setActiveLang(newLang);
      this.userlaneService.setLanguage(newLang);
      this.updateLanguageOptions();

      this.translocoService.load(newLang).subscribe({
        next: () => {
          this.finishFormUpdate();
        },
      });
    } else {
      await this.finishFormUpdate();
    }
  }

  private buildCustomerUpdateRequest(): UpdateCurrentCustomerUserRequestParams {
    const formValues = this.form.value;
    return {
      updateCustomerUserRequest: {
        firstName: formValues.firstName as string,
        lastName: formValues.lastName as string,
        phoneNumber: formValues.phoneNumber ?? '',
        email: formValues.email ?? '',
        tenantName: formValues.companyName ?? '',
        languageCode: formValues.languageCode as string,
      },
    };
  }

  private buildProviderUpdateRequest(): UpdateCurrentProviderUserRequestParams {
    const formValues = this.form.getRawValue();
    return {
      updateProviderUserRequest: {
        firstName: formValues.firstName as string,
        lastName: formValues.lastName as string,
        languageCode: formValues.languageCode as string,
        phoneNumber: formValues.phoneNumber ?? '',
      },
    };
  }

  openDialog() {
    this.dialogService.open(ChangePasswordDialogComponent, {
      header: translate('userProfile.changePassword.dialogHeader'),
      width: '30rem',
      modal: true,
      closable: true,
    });
  }

  updateLanguageOptions(): void {
    const activeLang = this.translocoService.getActiveLang();
    this.languageOptions = this.directusLoader.availableLanguages.map((lang: Language) => ({
      label: lang.localized_name[activeLang] || lang.localized_name['en'],
      value: lang.code,
    }));
  }

  get showNewsletterSubscription(): boolean {
    return (
      environment.newsletterEnabled &&
      (this.roleCheckService.hasProviderAdminRole() ||
        this.roleCheckService.hasProviderUserRole() ||
        this.roleCheckService.hasProviderManagerRole())
    );
  }

  get showCustomerAdminFields(): boolean {
    return !this.isProviderUser() && this.roleCheckService.hasCustomerAdminRole();
  }

  isProviderUser(): boolean {
    return (
      this.roleCheckService.hasProviderAdminRole() || this.roleCheckService.hasProviderUserRole()
    );
  }

  private getCurrentUserDataFromAPI(): Observable<CustomerUserResponse | ProviderUserResponse> {
    return this.isProviderUser()
      ? this.providerUserService.getCurrentProviderUser()
      : this.customerUserService.getCurrentCustomerUser();
  }

  private populateFormWithUserData(userData: CustomerUserResponse | ProviderUserResponse): void {
    const commonValues = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      languageCode: userData.languageCode,
      phoneNumber: userData.phoneNumber ?? '',
    };

    if (this.isProviderUser()) {
      this.scimManaged = (userData as ProviderUserResponse).scimManaged ?? false;
      this.form.patchValue(commonValues);
    } else {
      const customerUserData = userData as CustomerUserResponse;
      this.form.patchValue({
        ...commonValues,
        companyName: customerUserData.tenantName,
        email: customerUserData.email,
      });

      const billingForm = this.billingForm();
      if (this.showCustomerAdminFields && customerUserData.invoiceData && billingForm) {
        billingForm.form.patchValue({
          legalCompanyName: customerUserData.invoiceData.legalCompanyName,
          legalForm: customerUserData.invoiceData.legalForm,
          country: customerUserData.invoiceData.country,
          zipCode: customerUserData.invoiceData.zipCode,
          city: customerUserData.invoiceData.city,
          additionalInformation: customerUserData.invoiceData.additionalInformation,
          street: customerUserData.invoiceData.street,
          number: customerUserData.invoiceData.number,
          vatNumber: customerUserData.invoiceData.vatNumber,
          invoiceEmail: customerUserData.invoiceData.invoiceEmail,
          responsibleForInvoices: customerUserData.invoiceData.responsibleForInvoicesEmail,
          costCenter: customerUserData.invoiceData.costCenter,
          internalInformation: customerUserData.invoiceData.internalInformation,
        });
      }
    }
  }

  private toggleFormControlsState() {
    if (this.editing) {
      this.form.enable();
      if (this.scimManaged) {
        UserProfileComponent.IDENTITY_PROVIDER_FIELDS.forEach((field) =>
          this.form.get(field)?.disable(),
        );
      }
    } else {
      this.form.disable();
    }
  }

  private async finishFormUpdate(): Promise<void> {
    this.editing = false;
    this.toggleFormControlsState();
    await this.authService.updateToken();
    await this.userProfileService.fetchAndUpdateUserProfile();
    await this.toast.showSuccess('common.save.success.header', 'userProfile.edit.success.detail');
  }

  onEditBilling() {
    this.editingBilling = true;
    const billingForm = this.billingForm();
    if (billingForm) {
      billingForm.toggleFormControlsState(true);
    }
  }

  onSubmitBilling() {
    const billingForm = this.billingForm();
    if (!billingForm || billingForm.form.invalid) {
      billingForm?.form.markAllAsTouched();
      return;
    }

    this.submittingBilling = true;
    const request: UpdateCurrentCustomerUserInvoiceDataRequestParams = {
      invoiceDataRequest: billingForm.buildInvoiceDataRequest(),
    };

    this.customerUserService
      .updateCurrentCustomerUserInvoiceData(request)
      .pipe(
        finalize(() => {
          this.submittingBilling = false;
        }),
      )
      .subscribe({
        next: () => {
          this.handleBillingUpdateSuccess();
        },
      });
  }

  private async handleBillingUpdateSuccess(): Promise<void> {
    this.editingBilling = false;
    const billingForm = this.billingForm();
    if (billingForm) {
      billingForm.toggleFormControlsState(false);
    }
    await this.authService.updateToken();
    await this.userProfileService.fetchAndUpdateUserProfile();
    await this.toast.showSuccess('common.save.success.header', 'userProfile.edit.success.detail');
  }
}
